import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { FiCheck, FiCheckCircle, FiEdit2, FiGrid, FiPlus, FiSearch, FiSlash } from "react-icons/fi";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import POApprovalDialog from "../components/popups/POApprovalDialog";
import { serviceDepartmentsText } from "./serviceDepartmentsMessages";
import "../PurchasingUi.css";
import "./ServiceOrdersConfiguration.css";

const base = "/api/purchasing/service-orders/departments";

export default function ServiceOrdersConfiguration({ businessUnitId }) {
  const { themeId, languageId } = usePmsTheme();
  const t = (key, values) => serviceDepartmentsText(languageId, key, values);
  const formId = useId();
  const [departments, setDepartments] = useState(null);
  const [editor, setEditor] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const epoch = useRef(0);
  const lock = useRef(false);

  const load = useCallback(async () => {
    const current = ++epoch.current;
    lock.current = false;
    setBusy(false); setDepartments(null); setEditor(null); setSearch(""); setError(""); setNotice(null); setBlocked(false);
    try {
      const response = await axios.get(base, { params: { business_unit_id: businessUnitId } });
      if (current === epoch.current) setDepartments(response.data);
    } catch {
      if (current === epoch.current) setError("Departments could not be loaded.");
    }
  }, [businessUnitId]);

  useEffect(() => { load(); return () => { epoch.current += 1; }; }, [load]);
  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const save = async (value, closeEditor = false) => {
    if (lock.current || blocked || !value.name.trim()) return;
    const current = epoch.current;
    lock.current = true;
    setBusy(true); setError(""); setNotice(null);
    const payload = {
      name: value.name.trim(), description: (value.description || "").trim(),
      ...(value.id ? { revision: value.revision, is_active: value.is_active } : {}),
    };
    try {
      const { data: updated } = value.id
        ? await axios.patch(`${base}/${value.id}`, payload, { params: { business_unit_id: businessUnitId } })
        : await axios.post(base, payload, { params: { business_unit_id: businessUnitId } });
      if (current !== epoch.current) return;
      setDepartments((previous) => [...previous.filter((row) => row.id !== updated.id), updated]);
      if (closeEditor) setEditor(null);
      setNotice({ name: updated.name, key: closeEditor ? (value.id ? "Department updated: {name}" : "Department added: {name}") : (updated.is_active ? "Department activated: {name}" : "Department deactivated: {name}") });
    } catch (failure) {
      if (current !== epoch.current) return;
      if (failure.response?.data?.code === "SERVICE_DEPARTMENT_NAME_CONFLICT") {
        setError("A department with this name already exists.");
      } else if ([403, 409].includes(failure.response?.status)) {
        setBlocked(true);
        setError("Departments or permissions changed. Reload before saving again.");
      } else setError((failure.response?.status === 400 && failure.response?.data?.error) || "The department could not be saved.");
    } finally {
      if (current === epoch.current) { lock.current = false; setBusy(false); }
    }
  };

  const openEditor = (value = { name: "", description: "" }) => {
    setError(""); setNotice(null); setEditor({ ...value, description: value.description || "" });
  };
  const closeEditor = () => { setEditor(null); if (!blocked) setError(""); };
  const filtered = (departments || []).filter((row) => `${row.name} ${row.description || ""}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()))
    .sort((left, right) => left.name.localeCompare(right.name, languageId || "en"));
  const feedback = <>{error && <div className="svc-departments__error" role="alert">{t(error)}{blocked && <button type="button" className="svc-departments__button" onClick={load}>{t("Reload departments")}</button>}</div>}</>;

  return <section className="pilcore-purchasing-ui svc-departments" data-theme={themeId}>
    <header className="svc-departments__header">
      <div><h2><FiGrid aria-hidden="true" />{t("Service departments")}</h2><p>{t("Create the departments available for service orders.")}</p></div>
      <button type="button" className="svc-departments__button svc-departments__button--primary" disabled={!departments || busy || blocked} onClick={() => openEditor()}><FiPlus aria-hidden="true" />{t("Add")}</button>
    </header>
    {!editor && feedback}
    {!departments ? <div className="svc-departments__empty">{error ? <button type="button" className="svc-departments__button" onClick={load}>{t("Retry")}</button> : <p role="status">{t("Loading departments…")}</p>}</div> : <>
      <div className="svc-departments__toolbar">
        <label className="svc-departments__search"><FiSearch aria-hidden="true" /><input type="search" aria-label={t("Search departments")} placeholder={t("Search departments")} value={search} onChange={(event) => setSearch(event.target.value)} /></label>
        <span className="svc-departments__count">{t("{active} active · {total} total", { active: departments.filter((row) => row.is_active).length, total: departments.length })}</span>
      </div>
      {!filtered.length ? <div className="svc-departments__empty"><FiGrid aria-hidden="true" /><strong>{t(departments.length ? "No matching departments" : "No departments yet")}</strong><p>{t(departments.length ? "Try a different name or description." : "Add your first department to start organising service orders.")}</p></div> : <div className="svc-departments__viewport"><table className="svc-departments__table">
        <colgroup><col className="svc-departments__nameColumn" /><col /><col className="svc-departments__statusColumn" /><col className="svc-departments__actionsColumn" /></colgroup>
        <thead><tr><th scope="col">{t("Department")}</th><th scope="col">{t("Description")}</th><th scope="col">{t("Status")}</th><th scope="col">{t("Actions")}</th></tr></thead>
        <tbody>{filtered.map((row) => <tr key={row.id}>
          <th scope="row">{row.name}</th>
          <td className="svc-departments__description">{row.description || <span className="svc-departments__muted" aria-label={t("No description")}>—</span>}</td>
          <td><span className={`svc-departments__badge${row.is_active ? " svc-departments__badge--active" : ""}`}>{row.is_active ? <FiCheck aria-hidden="true" /> : <FiSlash aria-hidden="true" />}{t(row.is_active ? "Active" : "Inactive")}</span></td>
          <td><div className="svc-departments__actions">
            <button type="button" className="svc-departments__button svc-departments__iconButton" disabled={busy || blocked} onClick={() => openEditor(row)} aria-label={t("Edit department: {name}", { name: row.name })} title={t("Edit")}><FiEdit2 aria-hidden="true" /></button>
            <button type="button" className="svc-departments__button svc-departments__iconButton" disabled={busy || blocked} onClick={() => save({ ...row, is_active: !row.is_active })} aria-label={t(row.is_active ? "Deactivate department: {name}" : "Activate department: {name}", { name: row.name })} title={t(row.is_active ? "Deactivate" : "Activate")}>{row.is_active ? <FiSlash aria-hidden="true" /> : <FiCheckCircle aria-hidden="true" />}</button>
          </div></td>
        </tr>)}</tbody>
      </table></div>}
    </>}
    {notice && <div className="svc-departments__notice" role="status"><FiCheckCircle aria-hidden="true" />{t(notice.key, { name: notice.name })}</div>}
    <POApprovalDialog isOpen={!!editor} title={t(editor?.id ? "Edit department" : "Add department")} subtitle={t("Choose a name and optionally explain what this department handles.")} width="narrow" busy={busy} onClose={closeEditor} footer={<>
      <button type="button" className="svc-departments__button" disabled={busy} onClick={closeEditor}>{t("Cancel")}</button>
      <button type="submit" form={formId} className="svc-departments__button svc-departments__button--primary" disabled={busy || blocked || !editor?.name.trim()}>{t(busy ? "Saving…" : editor?.id ? "Save changes" : "Add department")}</button>
    </>}>
      {editor && <form id={formId} className="svc-departments__form" onSubmit={(event) => { event.preventDefault(); save(editor, true); }}>
        {feedback}
        <label className="svc-departments__field">{t("Department name")}<input required maxLength={150} autoComplete="off" value={editor.name} disabled={busy || blocked} onChange={(event) => setEditor({ ...editor, name: event.target.value })} /></label>
        <label className="svc-departments__field">{t("Description (optional)")}<textarea rows={4} maxLength={2000} value={editor.description} disabled={busy || blocked} onChange={(event) => setEditor({ ...editor, description: event.target.value })} /></label>
      </form>}
    </POApprovalDialog>
  </section>;
}
